import * as s from '~js/utils/selectors';
import * as a from '~js/utils/attributes';
import * as c from '~js/utils/classes';
import { QuickViewPopup } from '~js/components/quick-view-popup';
import { Zoom } from '~js/components/zoom';
import { ProductVideo } from '~js/components/product-video';
import {
  flickitySmoothScrolling,
} from '~js/utils/utils';

const Flickity = themeVendor.Flickity

export class Product {
  constructor(section) {
    this.container = section.container;
    this.sectionId = this.container.getAttribute(a.attributes$6.sectionId);
    this.tallLayout = this.container.getAttribute(a.attributes$6.dataTallLayout) === 'true';
    this.featureSliders = this.container.querySelectorAll(s.selectors$7.featureSlider);
    this.flkty = null;
    this.flktyNav = null;
    this.isFlickityDragging = false;
    this.enableHistoryState = !this.container.classList.contains(c.classes$7.featuredProduct);
    this.checkSliderOnResize = () => this.checkSlider();
    this.flktyNavOnResize = () => this.resizeFlickityNav();

    this.scrollToReviews();
    this.initUpsellSlider();
    this.initFeatureSlider();

    new QuickViewPopup(this.container);

    // Skip initialization of product form, slider and media functions if section has onboarding content only
    if (this.container.classList.contains(c.classes$7.featuredProductOnboarding)) {
      return;
    }

    // Record recently viewed products when the product page is loading
    const productJson = this.container.querySelector(s.selectors$7.productJson);
    if (productJson && productJson.innerHTML) {
      const productJsonHandle = JSON.parse(productJson.innerHTML).handle;
      let recentObj = {};
      if (productJsonHandle) {
        recentObj = {
          handle: productJsonHandle,
        };
      }
      Shopify.Products.recordRecentlyViewed(recentObj);
    } else {
      Shopify.Products.recordRecentlyViewed();
    }

    new Zoom(this.container);

    this.productSlider();
    this.initMediaSwitch();
    this.initProductVideo();
    this.initProductModel();
    this.initShopifyXrLaunch();
  }

  productSlider() {
    this.checkSlider();
    document.addEventListener('theme:resize:width', this.checkSliderOnResize);
  }

  checkSlider() {
    if (!this.tallLayout || window.innerWidth < theme.sizes.large) {
      this.initProductSlider();
      return;
    }

    this.destroyProductSlider();
  }

  resizeFlickityNav() {
    if (this.flktyNav !== null) {
      this.flktyNav.resize();
    }
  }

  /* Product Slider */
  initProductSlider() {
    const slider = this.container.querySelector(s.selectors$7.productMediaSlider);
    const thumbs = this.container.querySelector(s.selectors$7.productMediaThumbs);
    const media = this.container.querySelectorAll(s.selectors$7.productMediaWrapper);

    if (media.length > 1) {
      this.flkty = new Flickity(slider, {
        wrapAround: true,
        pageDots: false,
        adaptiveHeight: true,
        on: {
          ready: () => {
            slider.setAttribute(a.attributes$6.tabindex, '-1');

            media.forEach((item) => {
              if (!item.classList.contains(c.classes$7.isSelected)) {
                const links = item.querySelectorAll(s.selectors$7.links);
                if (links.length) {
                  links.forEach((link) => {
                    link.setAttribute(a.attributes$6.tabindex, '-1');
                  });
                }
              }
            });
          },
          dragStart: () => {
            slider.classList.add(c.classes$7.isMoving);
          },
          dragMove: () => {
            // Prevent lightbox trigger on dragMove
            this.isFlickityDragging = true;
          },
          staticClick: () => {
            this.isFlickityDragging = false;
          },
          settle: (index) => {
            const currentSlide = this.flkty.selectedElement;
            const mediaId = currentSlide.getAttribute(a.attributes$6.mediaId);

            this.flkty.cells.forEach((slide, i) => {
              const links = slide.element.querySelectorAll(s.selectors$7.links);
              if (links.length) {
                links.forEach((link) => {
                  link.setAttribute(a.attributes$6.tabindex, i === index ? '0' : '-1');
                });
              }
            });
            this.switchMedia(mediaId);
            slider.classList.remove(c.classes$7.isMoving);
          },
        },
      });

      // Toggle flickity draggable functionality based on media play/pause state
      if (media.length) {
        media.forEach((el) => {
          el.addEventListener('theme:media:play', () => {
            this.flkty.options.draggable = false;
            this.flkty.updateDraggable();
            el.closest(s.selectors$7.productMediaSlider).classList.add(c.classes$7.hasMediaActive);
          });

          el.addEventListener('theme:media:pause', () => {
            this.flkty.options.draggable = true;
            this.flkty.updateDraggable();
            el.closest(s.selectors$7.productMediaSlider).classList.remove(c.classes$7.hasMediaActive);
          });
        });
      }

      // iOS smooth scrolling fix
      flickitySmoothScrolling(slider);

      if (thumbs !== null) {
        this.flktyNav = new Flickity(thumbs, {
          asNavFor: slider,
          contain: true,
          pageDots: false,
          prevNextButtons: false,
          resize: true,
          on: {
            ready: () => {
              thumbs.setAttribute(a.attributes$6.tabindex, '-1');
            },
          },
        });

        if (this.flktyNav !== null) {
          document.addEventListener('theme:resize:width', this.flktyNavOnResize);
        }

        // iOS smooth scrolling fix
        flickitySmoothScrolling(thumbs);

        // Disable link click
        const thumbLinks = this.container.querySelectorAll(s.selectors$7.productSingleThumbnailLink);
        if (thumbLinks.length) {
          thumbLinks.forEach((el) => {
            el.addEventListener('click', (e) => {
              e.preventDefault();
            });
          });
        }
      }
    }
  }

  destroyProductSlider() {
    if (this.flkty !== null) {
      this.flkty.destroy();
      this.flktyNav.destroy();

      this.flkty = null;
      this.flktyNav = null;
    }
  }

  /* Upsell Products Slider */
  initUpsellSlider() {
    const slider = this.container.querySelector(s.selectors$7.upsellProductSlider);
    const items = this.container.querySelectorAll(s.selectors$7.upsellProduct);

    if (items.length > 1) {
      const flktyUpsell = new Flickity(slider, {
        wrapAround: true,
        pageDots: true,
        adaptiveHeight: true,
        prevNextButtons: false,
      });

      flktyUpsell.on('change', (index) => {
        flktyUpsell.cells.forEach((slide, i) => {
          const links = slide.element.querySelectorAll(s.selectors$7.links);
          if (links.length) {
            links.forEach((link) => {
              link.setAttribute(a.attributes$6.tabindex, i === index ? '0' : '-1');
            });
          }
        });
      });
    }
  }

  /* Feature Block Slider */
  initFeatureSlider() {
    this.featureSliders.forEach((featureSliders) => {
      const featureSlideIndex = Array.from(featureSliders.children);

      if (featureSlideIndex.length > 1) {
        this.flktyFeature = new Flickity(featureSliders, {
          wrapAround: true,
          pageDots: true,
          adaptiveHeight: true,
          prevNextButtons: false,
        });
      }
    });
  }

  handleMediaFocus(event) {
    // Do nothing unless ENTER or TAB key are pressed
    if (event.code !== theme.keyboardKeys.ENTER && event.code !== theme.keyboardKeys.TAB) {
      return;
    }

    const mediaId = event.currentTarget.getAttribute(a.attributes$6.thumbId);
    const activeSlide = this.container.querySelector(`[${a.attributes$6.mediaId}="${mediaId}"]`);
    const slideIndex = parseInt([...activeSlide.parentNode.children].indexOf(activeSlide));
    const slider = this.container.querySelector(s.selectors$7.productMediaSlider);
    const sliderNav = this.container.querySelector(s.selectors$7.productMediaThumbs);
    const flkty = Flickity.data(slider) || null;
    const flktyNav = Flickity.data(sliderNav) || null;

    // Go to the related slide media
    if (
      flkty &&
      flkty.isActive &&
      slideIndex > -1 &&
      (event.code === theme.keyboardKeys.ENTER || event.code === theme.keyboardKeys.NUMPADENTER)
    ) {
      flkty.select(slideIndex);
    }

    // Move thumbs to the selected one
    if (flktyNav && flktyNav.isActive && slideIndex > -1) {
      flktyNav.select(slideIndex);
    }
  }

  switchMedia(mediaId) {
    const mediaItems = document.querySelectorAll(`${s.selectors$7.productMediaWrapper}`);
    const selectedMedia = this.container.querySelector(
      `${s.selectors$7.productMediaWrapper}[${a.attributes$6.mediaId}="${mediaId}"]`
    );
    const isFocusEnabled = !document.body.classList.contains(c.classes$7.noOutline);

    // Pause other media
    if (mediaItems.length) {
      mediaItems.forEach((media) => {
        media.dispatchEvent(new CustomEvent('theme:media:hidden'), { bubbles: true });
        media.classList.add(c.classes$7.mediaHidden);
      });
    }

    if (isFocusEnabled) {
      selectedMedia.focus();
    }

    selectedMedia.closest(s.selectors$7.productMediaSlider).classList.remove(c.classes$7.hasMediaActive);
    selectedMedia.classList.remove(c.classes$7.mediaHidden);
    selectedMedia.dispatchEvent(new CustomEvent('theme:media:visible'), { bubbles: true });

    // If media is not loaded, trigger poster button click to load it
    const deferredMedia = selectedMedia.querySelector(s.selectors$7.deferredMedia);
    if (deferredMedia && deferredMedia.getAttribute(a.attributes$6.loaded) !== 'true') {
      selectedMedia.querySelector(s.selectors$7.deferredMediaButton).dispatchEvent(new Event('click'));
    }
  }

  initMediaSwitch() {
    const productThumbImages = this.container.querySelectorAll(s.selectors$7.productMediaThumb);
    if (productThumbImages.length) {
      productThumbImages.forEach((el) => {
        el.addEventListener('keyup', this.handleMediaFocus.bind(this));
        el.addEventListener('click', (e) => {
          e.preventDefault();
        });
      });
    }
  }

  initProductVideo() {
    this.videos = new ProductVideo(this.container);
  }

  initProductModel() {
    const modelItems = this.container.querySelectorAll(s.selectors$7.productModel);
    if (modelItems.length) {
      modelItems.forEach((element) => {
        theme.ProductModel.init(element, this.sectionId);
      });
    }
  }

  initShopifyXrLaunch() {
    document.addEventListener('shopify_xr_launch', () => {
      const currentMedia = this.container.querySelector(
        `${s.selectors$7.productModel}:not(.${c.classes$7.mediaHidden})`
      );
      currentMedia.dispatchEvent(new CustomEvent('xrLaunch'));
    });
  }

  onUnload() {
    if (this.flktyNav !== null) {
      document.removeEventListener('theme:resize:width', this.flktyNavOnResize);
    }

    document.removeEventListener('theme:resize:width', this.checkSliderOnResize);
  }

  scrollToReviews() {
    const productRating = this.container.querySelector(s.selectors$7.productRating);
    const events = ['click', 'keydown'];

    if (!productRating) {
      return;
    }

    events.forEach((eventName) => {
      productRating.addEventListener(eventName, (event) => {
        if (
          (event.code !== theme.keyboardKeys.ENTER && event.code !== theme.keyboardKeys.NUMPADENTER) ||
          event.type != 'click'
        ) {
          const reviewsContainer = document.querySelector(s.selectors$7.productReviews);

          if (!reviewsContainer) {
            return;
          }

          reviewsContainer.scrollIntoView({ behavior: 'smooth' });
        }
      });
    });
  }

  onBlockSelect(event) {
    const flkty = Flickity.data(event.target.closest(s.selectors$7.featureSlider));
    const index = parseInt([...event.target.parentNode.children].indexOf(event.target));

    if (flkty) {
      flkty.select(index);
    }
  }
}